/* global FormData */

import { buildApiUrl } from "../config";
import { withCredentials } from "./apiClient";
import { fetchWithRetry, checkAuthStatus, parseErrorResponse } from "../utils";

const PPTX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.presentationml.presentation";
const JSON_MIME_TYPE = "application/json";

export type GoldenArtifactLabel =
  | "before"
  | "after"
  | "after_polish"
  | "llm_input"
  | "operations"
  | "prepared_batch_operations"
  | "tool_call"
  | "tool_output"
  | "tool_trace"
  | "deck_plan";

export interface GoldenArtifactRun {
  runId: string;
  startedAt: string;
}

export interface GoldenArtifactUploadOptions {
  /**
   * Optional sub-folder under `runId/` to write into. Validated server-side
   * against `^[A-Za-z0-9._-]{1,64}$` so it is safe to use as a path segment.
   * Used by the deck-creation flow to group per-slide artifacts (e.g.
   * `slide_00`, `slide_01`) under a single deck-level run folder, while
   * keeping the top-level `before.pptx` / `after.pptx` / `deck_plan.json`
   * unscoped.
   */
  subdirectory?: string;
}

function toPadded(value: number, width = 2): string {
  return String(value).padStart(width, "0");
}

export function createGoldenArtifactRun(now = new Date()): GoldenArtifactRun {
  const runId = [
    "golden",
    now.getUTCFullYear(),
    toPadded(now.getUTCMonth() + 1),
    toPadded(now.getUTCDate()),
    "T",
    toPadded(now.getUTCHours()),
    toPadded(now.getUTCMinutes()),
    toPadded(now.getUTCSeconds()),
    "_",
    toPadded(now.getUTCMilliseconds(), 3),
  ].join("");

  return {
    runId,
    startedAt: now.toISOString(),
  };
}

function decodeBase64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const arrayBuffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(arrayBuffer).set(bytes);
  return arrayBuffer;
}

function toArtifactFilename(runId: string, label: GoldenArtifactLabel): string {
  const extension =
    label === "before" || label === "after" || label === "after_polish" ? "pptx" : "json";
  return `${runId}.${label}.${extension}`;
}

async function uploadGoldenArtifactBlob(
  runId: string,
  label: GoldenArtifactLabel,
  blob: Blob,
  options?: GoldenArtifactUploadOptions
): Promise<string> {
  const filename = toArtifactFilename(runId, label);
  const formData = new FormData();
  formData.append("label", label);
  if (options?.subdirectory) {
    formData.append("subdirectory", options.subdirectory);
  }
  formData.append("file", blob, filename);

  const response = await fetchWithRetry(
    buildApiUrl(`/api/golden-artifacts/runs/${encodeURIComponent(runId)}/artifacts`),
    withCredentials({
      method: "POST",
      body: formData,
    }),
    1,
    "GOLDEN_ARTIFACTS"
  );
  checkAuthStatus(response);

  if (!response.ok) {
    const message = await parseErrorResponse(response);
    throw new Error(`Golden artifact upload failed (${response.status}): ${message}`);
  }

  return filename;
}

export async function uploadGoldenArtifactPptx(
  runId: string,
  label: "before" | "after" | "after_polish",
  pptxBase64: string,
  options?: GoldenArtifactUploadOptions
): Promise<string> {
  if (!pptxBase64) {
    throw new Error(`Missing PPTX payload for ${label} artifact`);
  }
  const bytes = decodeBase64ToUint8Array(pptxBase64);
  const blob = new Blob([toArrayBuffer(bytes)], { type: PPTX_MIME_TYPE });
  return uploadGoldenArtifactBlob(runId, label, blob, options);
}

export async function uploadGoldenArtifactJson(
  runId: string,
  label:
    | "llm_input"
    | "operations"
    | "prepared_batch_operations"
    | "tool_call"
    | "tool_output"
    | "tool_trace"
    | "deck_plan",
  payload: unknown,
  options?: GoldenArtifactUploadOptions
): Promise<string> {
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: JSON_MIME_TYPE });
  return uploadGoldenArtifactBlob(runId, label, blob, options);
}

